import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Compass, Send, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => sessionStorage.getItem("admin-token") || localStorage.getItem("adminToken") || "";
const getSid = () => {
  let s = localStorage.getItem("admin-helper-sid");
  if (!s) { s = `ah-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("admin-helper-sid", s); }
  return s;
};
const SUGGEST = ["Πώς βάζω βίντεο μεταξύ εβδομάδων;", "Πού βλέπω ποιος άνοιξε το SMS;", "Πώς δουλεύει το ερωτηματολόγιο Εβδ. 4;", "Τι κάνει το tab Niche;"];

// Βοηθός admin: εξηγεί tabs/σελίδες/ροές. Μόνο σε /admin και /8w01, όχι μέσα σε iframe.
export function AdminAssistant() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasToken, setHasToken] = useState(!!token());
  const endRef = useRef(null);
  const show = (pathname.startsWith("/admin") || pathname.startsWith("/8w01")) && !pathname.startsWith("/admin/program-preview") && window.self === window.top;

  useEffect(() => { setHasToken(!!token()); }, [pathname, open]);
  useEffect(() => {
    if (!open || !token()) return;
    fetch(`${API}/admin/assistant-helper/history/${getSid()}`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.json()).then((d) => setMsgs((m) => (m.length ? m : d.messages || []))).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  if (!show || !hasToken) return null;

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/assistant-helper/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ session_id: getSid(), message: text, page: pathname + window.location.search }) });
      const reader = r.body.getReader(); const dec = new TextDecoder(); let buf = ""; let reply = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line || line === "[DONE]") continue;
          try { reply += JSON.parse(line).t || ""; } catch { /* ignore */ }
          setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: reply }]);
        }
      }
      if (!reply) setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Δεν μπόρεσα να απαντήσω. Δοκίμασε ξανά." }]);
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Πρόβλημα σύνδεσης. Δοκίμασε ξανά σε λίγο." }]);
    } finally { setBusy(false); }
  };
  const clear = async () => {
    await fetch(`${API}/admin/assistant-helper/history/${getSid()}`, { method: "DELETE", headers: { Authorization: `Bearer ${token()}` } }).catch(() => {});
    setMsgs([]);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="admin-helper-open" aria-label="Βοηθός admin"
          className="fixed bottom-4 right-3 z-[75] flex items-center gap-2 pl-2.5 pr-3.5 py-2 rounded-full bg-[#1E3A5F] text-white shadow-lg ring-2 ring-amber-400/70 hover:brightness-110 transition">
          <Compass className="w-4 h-4 text-amber-300" /><span className="text-xs font-extrabold">Πώς δουλεύει;</span>
        </button>
      )}
      {open && (
        <div data-testid="admin-helper-panel" className="fixed bottom-2 right-2 left-2 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[400px] max-w-[calc(100vw-1rem)] z-[75] h-[80dvh] sm:h-[600px] max-h-[90dvh] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#1E3A5F] text-white">
            <Compass className="w-5 h-5 text-amber-300" />
            <div className="min-w-0"><div className="text-sm font-black leading-tight">Βοηθός admin</div><div className="text-[11px] text-white/70">Πού είναι τι & πώς το χρησιμοποιώ</div></div>
            <button onClick={clear} className="ml-auto p-1.5 rounded-lg hover:bg-white/10" data-testid="admin-helper-clear" aria-label="Καθάρισε" title="Νέα συζήτηση"><Trash2 className="w-4 h-4" /></button>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="admin-helper-close" aria-label="Κλείσιμο"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50" data-testid="admin-helper-messages">
            {!msgs.length && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">Ρώτα με οτιδήποτε για το admin — tabs, σελίδες, βήματα. Ξέρω πώς δουλεύει, όχι τα ζωντανά νούμερα.</p>
                {SUGGEST.map((q) => <button key={q} onClick={() => send(q)} data-testid="admin-helper-suggest" className="block w-full text-left text-sm px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-amber-400">{q}</button>)}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[92%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "ml-auto bg-[#1E3A5F] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`} data-testid={`admin-helper-msg-${m.role}`}>
                {m.text || <span className="inline-flex gap-1 items-center text-slate-400"><span className="animate-pulse">●</span><span className="animate-pulse [animation-delay:150ms]">●</span><span className="animate-pulse [animation-delay:300ms]">●</span></span>}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-2 border-t border-slate-200 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="π.χ. πώς κρύβω ένα βίντεο σε κουμπί;" data-testid="admin-helper-input"
              className="flex-1 px-3 py-2.5 rounded-xl border border-slate-300 focus:border-[#1E3A5F] outline-none text-sm" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="admin-helper-send" className="p-2.5 rounded-xl bg-amber-400 text-[#1E3A5F] disabled:opacity-40" aria-label="Στείλε"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
